/**
 * Postclose performance snapshot (acquisitions only).
 *
 * Buying a seasoned loan means the pay history matters before we ever fund. These
 * are the `category: 'performance'` fields on LoanAcquisitionBaseModel:
 *   assetStatus         — Performing / Sub-Performing / Non-Performing / REO / Paid Off
 *   performingIndicator — the boolean the trade desk keys on
 *   paymentsMade        — seasoning: payments made at the correspondent
 *   lastPaidInstallmentDate (LPI) / nextPaymentDueDate / firstPaymentDate
 *   maturityDate        — per the note (stored, not derived, for a purchased loan)
 *   boardingDate        — when it lands on the servicer
 *
 * Saves via update_loan_data/{dealId} (pipeline-aware through PipelineContext).
 *
 * Renders as a `LoanSectionCard` — a peer section under the tab's page header.
 */
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import MonitorHeartOutlined from "@mui/icons-material/MonitorHeartOutlined";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import LoanSectionCard from "../../shared/tabLayout/LoanSectionCard";
import { TAB_TOKENS as T, orDash } from "../../shared/tabLayout/tabTokens";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";

// Mirrors asset_status_options in app/models/loan_acquisitions.py.
const ASSET_STATUSES = [
  "Performing",
  "Sub-Performing",
  "Non-Performing",
  "REO",
  "Paid Off",
];

const ASSET_STATUS_STYLE = {
  Performing: { background: T.color.okSoft, color: T.color.okInk },
  "Sub-Performing": { background: T.color.warnSoft, color: T.color.warnInk },
  "Non-Performing": { background: T.color.dangerSoft, color: T.color.dangerInk },
  REO: { background: T.color.dangerSoft, color: T.color.dangerInk },
  "Paid Off": { background: "#f1f5f9", color: T.color.ink2 },
};

const DATES = [
  { key: "lastPaidInstallmentDate", label: "Last paid installment (LPI)" },
  { key: "nextPaymentDueDate", label: "Next payment due" },
  { key: "firstPaymentDate", label: "First payment (per note)" },
  { key: "maturityDate", label: "Maturity (per note)" },
  { key: "boardingDate", label: "Boarded to servicer" },
];

// The six stored figures shown in the read-only fact grid, in file order:
// seasoning first, then the note/servicing dates.
const FACTS = (loan) => [
  {
    key: "paymentsMade",
    label: "Payments made",
    value: orDash(loan?.paymentsMade),
  },
  ...DATES.map((d) => ({
    key: d.key,
    label: d.label,
    value: formatDate(loan?.[d.key], "—"),
  })),
];

const PerformanceSnapshotCard = () => {
  const dispatch = useDispatch();
  const { updateLoanData } = useUnderwritingHook();
  const loan = useSelector((state) => state.loanDrilldown);
  const dealId = loan?._id;

  const [saving, setSaving] = useState(null);
  const [payments, setPayments] = useState(null);

  const save = useCallback(
    async (data, key) => {
      if (!dealId) return;
      setSaving(key);
      await updateLoanData({
        loanId: dealId,
        data,
        onSuccessMsg: "Performance updated",
        onSuccessFn: (updated) => {
          if (updated?._id) dispatch(loanDrilldownSet(updated));
        },
        onCompleteFn: () => setSaving(null),
      });
    },
    [dealId, updateLoanData, dispatch],
  );

  const assetStatus = loan?.assetStatus;

  return (
    <LoanSectionCard
      icon={<MonitorHeartOutlined />}
      title="Performance snapshot"
      subTitle="Pay history and seasoning on the loan as it performed at the correspondent."
      controls={
        assetStatus ? (
          <Chip
            size="small"
            label={assetStatus}
            sx={{
              height: 21,
              fontSize: 11,
              fontWeight: 600,
              ...(ASSET_STATUS_STYLE[assetStatus] || {}),
            }}
          />
        ) : null
      }
      disableBodyPadding
    >
      {/* Read-only roll-up of the stored figures as a bordered 6-up fact grid,
          collapsing to 3-up below 960px. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(6, 1fr)",
          borderBottom: `1px solid ${T.color.border}`,
          "@media (max-width: 960px)": { gridTemplateColumns: "repeat(3, 1fr)" },
        }}
      >
        {FACTS(loan).map((f, i) => (
          <Flx
            column
            key={f.key}
            sx={{
              gap: "4px",
              padding: "14px 16px",
              minWidth: 0,
              borderLeft: i === 0 ? "none" : `1px solid ${T.color.border}`,
              "@media (max-width: 960px)": {
                borderLeft: i % 3 === 0 ? "none" : `1px solid ${T.color.border}`,
                borderTop: i >= 3 ? `1px solid ${T.color.border}` : "none",
              },
            }}
          >
            <Txt sx={T.type.fieldLabel}>{f.label}</Txt>
            <Txt
              sx={{
                ...T.type.fieldValue,
                color: f.value === "—" ? T.color.muted : T.color.ink,
              }}
            >
              {f.value}
            </Txt>
          </Flx>
        ))}
      </Box>

      {/* Editable controls. */}
      <Flx
        sx={{
          gap: "22px",
          padding: "16px 18px",
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <TextField
          select
          size="small"
          label="Asset status"
          value={assetStatus || ""}
          disabled={saving === "assetStatus"}
          onChange={(e) => save({ assetStatus: e.target.value }, "assetStatus")}
          sx={{ minWidth: 200 }}
          InputProps={{ sx: { fontSize: 13 } }}
          InputLabelProps={{ sx: { fontSize: 13 } }}
        >
          {ASSET_STATUSES.map((s) => (
            <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>
              {s}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          label="Payments made"
          type="number"
          value={payments ?? loan?.paymentsMade ?? ""}
          disabled={saving === "paymentsMade"}
          onChange={(e) => setPayments(e.target.value)}
          onBlur={() => {
            if (payments === null || payments === "") return;
            const n = Number(payments);
            if (Number.isNaN(n) || n === loan?.paymentsMade) return;
            save({ paymentsMade: n }, "paymentsMade").then(() =>
              setPayments(null),
            );
          }}
          sx={{ minWidth: 160 }}
          InputProps={{ sx: { fontSize: 13 } }}
          InputLabelProps={{ sx: { fontSize: 13 } }}
        />

        <Flx ac sx={{ gap: "12px", marginLeft: "auto", paddingBottom: "4px" }}>
          <Flx column sx={{ gap: "2px" }}>
            <Txt sx={{ ...T.type.fieldValue, color: T.color.ink2 }}>
              Performing
            </Txt>
            <Txt sx={T.type.meta}>The indicator the trade desk keys on</Txt>
          </Flx>
          <Switch
            size="small"
            checked={loan?.performingIndicator === true}
            disabled={saving === "performingIndicator"}
            onChange={(e) =>
              save(
                { performingIndicator: e.target.checked },
                "performingIndicator",
              )
            }
          />
        </Flx>
      </Flx>
    </LoanSectionCard>
  );
};

export default PerformanceSnapshotCard;
